import React from 'react'
import NextHead from 'next/head'
import { useRouter } from 'next/router'
import { SITE_NAME, SITE_URL, SeoProps, absUrl, ldJson } from '@/utils/seo'

const DEFAULT_TITLE = '灵缇赛事平台'
const DEFAULT_DESCRIPTION = '灵缇赛事系统支持匹克球/网球/羽毛球/乒乓球等球拍类小组淘汰赛、双打/单打赛、循环赛，以及掼蛋/桥牌/升级等棋牌类赛事的一键编排、自动晋级与冠亚季季军判定。'
const DEFAULT_KEYWORDS = [
  // 球拍类
  '匹克球', '匹克球赛事', '匹克球比赛', 'pickleball',
  '网球赛事', '羽毛球赛事', '乒乓球赛事',
  '小组淘汰赛', '球拍类小组淘汰赛', '单打赛', '双打赛', '循环赛',
  '比赛编排', '一键编排', '自动晋级', '冠亚季',
  // 棋牌类
  '掼蛋', '掼蛋比赛', '掼蛋赛事', '掼蛋编排', '掼蛋平台', '掼蛋赛事平台',
  '苏州掼蛋', '江苏掼蛋', '淮安掼蛋', '掼蛋世界', '桥牌', '升级', '斗地主',
  // 品牌
  '灵缇', '灵缇互娱', '灵缇赛事系统',
].join(',')
const DEFAULT_OG_IMAGE = `${SITE_URL}/lingti-gd-logo.png`

// component is `Component` from _app.tsx (NextPageWithLayout) with optional static props .title / .description / .keywords / .ogImage / .noindex set on the page component
// seo 来自 pageProps.seo(服务端取到数据后按每页生成),优先级高于页面组件上的静态属性
const Head = ({ component = {} as any, seo = {} }: { component?: any; seo?: SeoProps }) => {
  const router = useRouter()
  const title = seo.title || component.title || DEFAULT_TITLE
  const description = seo.description || component.description || DEFAULT_DESCRIPTION
  const keywords = seo.keywords || component.keywords || DEFAULT_KEYWORDS
  const ogImage = absUrl(seo.image || component.ogImage || DEFAULT_OG_IMAGE)
  const canonical = seo.path ? absUrl(seo.path) : `${SITE_URL}${router.asPath?.split(/[?#]/)[0] || '/'}`
  const noindex = seo.noindex ?? component.noindex
  const jsonLd = seo.jsonLd ? ([] as any[]).concat(seo.jsonLd) : []
  return (
    <NextHead>
      <meta charSet="UTF-8" />
      <title>{title}</title>
      <meta httpEquiv="Content-Type" content="text/html;charset=utf-8" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
      <meta content="always" name="referrer" />
      <meta name="renderer" content="webkit" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="applicable-device" content="pc,mobile" />
      <meta name="viewport" content="width=device-width, viewport-fit=cover, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no" />
      <meta name="keywords" content={keywords} />
      <meta name="description" content={description} />
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />}
      {/* Canonical */}
      <link rel="canonical" href={canonical} key="canonical" />
      {/* Open Graph */}
      <meta property="og:type" content={seo.type || 'website'} />
      {seo.publishedTime && <meta property="article:published_time" content={seo.publishedTime} />}
      {seo.modifiedTime && <meta property="article:modified_time" content={seo.modifiedTime} />}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="zh_CN" />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {/* Icons */}
      <link rel="icon" href="/lingti-gd.ico" />
      <link rel="apple-touch-icon" href="/lingti-gd-logo.png" />
      <link rel="alternate" type="application/rss+xml" title="灵缇资讯" href="/rss.xml" />
      {jsonLd.map((d, i) => (
        // eslint-disable-next-line react/no-danger, react/no-array-index-key
        <script key={`ld-${i}`} type="application/ld+json" dangerouslySetInnerHTML={{ __html: ldJson(d) }} />
      ))}
    </NextHead>
  )
}

export default Head
